import React from 'react'
import { Form, Row, Col, DropdownButton, Dropdown, Button } from 'react-bootstrap'

// definitions
import { AccountXeroOrgWithData } from '../../../../screens/subsync/subsync'
import { XeroAPIContact } from '../../../../../../back-end/utilities/apiDefinitions'
import { DetailedStripeOrgAccountXeroOrg, Error } from '../settings'
import { validateEmailAddressFormat } from '../functions'

interface GeneralSettingsProps {
	orgMapping: DetailedStripeOrgAccountXeroOrg | undefined
	stripeOrgID: string
	mappedXeroOrg: AccountXeroOrgWithData | undefined
	handleOrgUpdate: (stripeOrgID: string, name: string, value: string) => void
	handleSettingsUpdate: (stripeOrgID: string, values: { [name: string]: string | boolean | XeroAPIContact | undefined }) => void
	errors: Error[]
}

const GeneralSettings = (props: GeneralSettingsProps) => {
	const [taxFilter, setTaxFilter] = React.useState<string>('')
	const alertEmail = props.orgMapping?.stripeOrgAccountXeroOrgSettings?.alertRecipientEmail || ''
	const isEmailValid = validateEmailAddressFormat(alertEmail)
	const [showValidHighlight, setShowValidHighlight] = React.useState(false)

	// Show green highlight for 5 seconds after change
	const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		props.handleSettingsUpdate(props.stripeOrgID, { [(e.target as HTMLInputElement).name]: (e.target as HTMLInputElement).value })
		if (validateEmailAddressFormat(e.target.value)) {
			setShowValidHighlight(true)
			setTimeout(() => setShowValidHighlight(false), 5000)
		} else {
			setShowValidHighlight(false)
		}
	}

	return (
		<>
			<Form.Group as={Row} className="mb-4">
				<Col sm="2">
					<Form.Label className="mr-3">SubSync processing type</Form.Label>
				</Col>
				<Col className="d-flex justify-content-start">
					<Button
						id={`${props.stripeOrgID}-mapping-type-account`}
						variant="outline-secondary"
						className={'option-button ' + (props.orgMapping?.stripeOrgAccountXeroOrgProcessingType === 'Standard' ? 'active' : '')}
						onClick={() => props.handleOrgUpdate(props.stripeOrgID!, 'stripeOrgAccountXeroOrgProcessingType', 'Standard')}
					>
						Standard
					</Button>
					<Button
						id={`${props.stripeOrgID}-mapping-type-item`}
						variant="outline-secondary"
						className={'option-button ' + (props.orgMapping?.stripeOrgAccountXeroOrgProcessingType === 'Rollup' ? 'active' : '')}
						onClick={() => props.handleOrgUpdate(props.stripeOrgID!, 'stripeOrgAccountXeroOrgProcessingType', 'Rollup')}
					>
						Summary
					</Button>
				</Col>
			</Form.Group>
			{props.orgMapping?.stripeOrgAccountXeroOrgProcessingType === 'Standard' && (
				<>
					<Form.Group as={Row}>
						<Col sm={2}>
							<Form.Label>Ignore $0 Stripe invoices</Form.Label>
						</Col>
						<Col>
							<Form.Check
								type={'switch'}
								id={`${props.stripeOrgID}-create-zero-invoices-disabled`}
								name={'createZeroAmountInvoicesDisabled'}
								checked={!!props.orgMapping?.stripeOrgAccountXeroOrgSettings?.createZeroAmountInvoicesDisabled}
								onChange={(e) => props.handleSettingsUpdate(props.stripeOrgID, { [e.target.name]: e.target.checked })}
							/>
						</Col>
					</Form.Group>
					<Form.Group as={Row} className="mb-4">
						<Col sm={2}>
							<Form.Label>Ignore unpaid Stripe invoices</Form.Label>
						</Col>
						<Col>
							<Form.Check
								type={'switch'}
								id={`${props.stripeOrgID}-create-unpaid-invoices-disabled`}
								name={'createUnpaidInvoicesDisabled'}
								checked={!!props.orgMapping?.stripeOrgAccountXeroOrgSettings?.createUnpaidInvoicesDisabled}
								onChange={(e) => props.handleSettingsUpdate(props.stripeOrgID, { [e.target.name]: e.target.checked })}
							/>
						</Col>
					</Form.Group>
					<Form.Group as={Row} className="mb-4">
						<Col sm={2}>
							<Form.Label>Ignore invoices created before SubSync</Form.Label>
						</Col>
						<Col>
							<Form.Check
								type={'switch'}
								id={`${props.stripeOrgID}-ignore-pre-subsync-invoices-enabled`}
								name={'ignorePreSubsyncInvoicesEnabled'}
								checked={!!props.orgMapping?.stripeOrgAccountXeroOrgSettings?.ignorePreSubsyncInvoicesEnabled}
								onChange={(e) => props.handleSettingsUpdate(props.stripeOrgID, { [e.target.name]: e.target.checked })}
							/>
						</Col>
					</Form.Group>
				</>
			)}
			<Form.Group as={Row}>
				<Col>Choose a 0.00% Xero tax rate to use when Stripe does not calculate tax (e.g. in unsupported jurisdictions):</Col>
			</Form.Group>
			<Form.Group as={Row} style={{ height: '2.5rem' }}>
				<Form.Label column sm={2}>
					Xero Tax Rate
				</Form.Label>
				<Col sm={4}>
					<DropdownButton
						id={`${props.stripeOrgID}-zero-percent-tax-rate-select`}
						title={
							props.mappedXeroOrg!.taxRates.find(
								(tr) => tr.xeroTaxRateID === props.orgMapping?.stripeOrgAccountXeroOrgSettings?.zeroPercentXeroTaxRateID
							)?.name || 'Select Xero Tax Rate'
						}
						onSelect={(eventKey) => props.handleSettingsUpdate(props.stripeOrgID, { zeroPercentXeroTaxRateID: eventKey! })}
					>
						<Dropdown.Item as="div">
							<Form.Control
								type="text"
								placeholder="Search or enter value"
								value={taxFilter}
								onClick={(e: React.MouseEvent<HTMLInputElement>) => e.stopPropagation()} // Prevent dropdown from closing on input click
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTaxFilter(e.target.value)} // Handle input changes
							/>
						</Dropdown.Item>
						{props
							.mappedXeroOrg!.taxRates.filter(
								(tr) => tr.canApplyToRevenue && Number(tr.effectiveRate) === 0 && tr.name!.toLowerCase().includes(taxFilter.toLowerCase())
							)
							.map((taxRate) => (
								<Dropdown.Item key={taxRate.xeroTaxRateID} eventKey={taxRate.xeroTaxRateID}>
									{taxRate.name} {taxRate.effectiveRate ? `(${(Number(taxRate.effectiveRate) * 100).toFixed(2)}%)` : ''}
								</Dropdown.Item>
							))}
					</DropdownButton>
				</Col>
			</Form.Group>
			<Form.Group as={Row} style={{ height: '2.5rem' }}>
				<Form.Label column sm={2}>
					Alerts Recipient Email
				</Form.Label>
				<Col sm={4}>
					<Form.Control
						type="email"
						className={`form-control ${alertEmail ? (!isEmailValid ? 'is-invalid' : showValidHighlight ? 'is-valid' : '') : ''}`}
						placeholder="Enter alternative email for SubSync alerts"
						value={alertEmail}
						onClick={(e: React.MouseEvent<HTMLInputElement, MouseEvent>) => e.stopPropagation()}
						onChange={handleEmailChange}
						name={'alertRecipientEmail'}
						isInvalid={!!alertEmail && !isEmailValid}
					/>
					{!!alertEmail && !isEmailValid ? <Form.Control.Feedback type="invalid">Invalid email format</Form.Control.Feedback> : null}
				</Col>
			</Form.Group>
		</>
	)
}

export { GeneralSettings }
